// #1337 (D-CAT-10) — multi-valued picker over a controlled-vocabulary option list.
// Same Popover + Command + CommandCheck shape as ManufacturerMultiCombobox, but
// data-agnostic: the caller passes the options (useFieldOptions) and owns the value.
// Clicking toggles membership and the popover STAYS OPEN.

import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button, ButtonArrow } from '@marine/ui/button';
import {
  Command,
  CommandCheck,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@marine/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@marine/ui/popover';
import { ScrollArea } from '@marine/ui/scroll-area';
import { humanizeToken } from '@/modules/core/utils/vocabulary';

/**
 * The aria attributes a `FormControl` slot injects onto its child (`aria-describedby`,
 * `aria-invalid`) and any the caller sets: they must reach the trigger `Button`, or the
 * form's description/error text is never announced for this field.
 */
export interface VocabularyMultiSelectProps extends React.AriaAttributes {
  value: string[];
  onChange: (values: string[]) => void;
  options: ReadonlyArray<{ readonly value: string; readonly label: string }>;
  id?: string;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  'aria-label'?: string;
}

export function VocabularyMultiSelect({
  value,
  onChange,
  options,
  id,
  disabled = false,
  loading = false,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  'aria-label': ariaLabel,
  ...rest
}: VocabularyMultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  // An off-list current value (a user term not yet in the loaded catalogue, or a
  // legacy token) still renders, humanized, so the dealer can see and untick it.
  const displayOptions = React.useMemo(() => {
    const known = new Set(options.map((o) => o.value));
    const extra = value.filter((v) => !known.has(v)).map((v) => ({ value: v, label: humanizeToken(v) }));
    return [...options, ...extra];
  }, [options, value]);

  const trimmed = search.trim().toLowerCase();
  const filtered = trimmed === '' ? displayOptions : displayOptions.filter((o) => o.label.toLowerCase().includes(trimmed));
  const selectedLabels = value.map((v) => displayOptions.find((o) => o.value === v)?.label ?? humanizeToken(v));
  const triggerLabel = selectedLabels.length > 0 ? selectedLabels.join(', ') : placeholder;

  const toggle = (next: string) => {
    onChange(value.includes(next) ? value.filter((v) => v !== next) : [...value, next]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          mode="input"
          placeholder={selectedLabels.length === 0}
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={disabled || loading}
          className="w-full justify-between"
          {...rest}
        >
          <span className={cn('truncate', selectedLabels.length === 0 && 'text-muted-foreground')}>
            {loading && selectedLabels.length === 0 ? 'Loading…' : triggerLabel}
          </span>
          <ButtonArrow />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder={searchPlaceholder} value={search} onValueChange={setSearch} />
          <CommandList>
            <ScrollArea viewportClassName="max-h-[260px] [&>div]:block!">
              <CommandEmpty>No matches.</CommandEmpty>
              <CommandGroup>
                {filtered.map((opt) => {
                  const selected = value.includes(opt.value);
                  return (
                    // aria-checked: membership must be announced, not only shown by the tick (WCAG 1.3.1 / 4.1.2).
                    <CommandItem key={opt.value} value={opt.value} aria-checked={selected} onSelect={() => toggle(opt.value)}>
                      <span className="truncate">{opt.label}</span>
                      <CommandCheck className={cn(selected ? 'opacity-100' : 'opacity-0')} />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
